import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { ArrowRightLeft } from "lucide-react";
import { api } from "../lib/api";

// Read-only Yacht details modal. Shows the full record + nested tenders /
// engines / generators with cover photos. Shared by the Yachts page and the
// supplier Dashboard so both surfaces show identical details.
export default function YachtDetailsDialog({ yacht, onClose }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const fileUrl = (p) => `${API}/api/files/${p}`;

  // Ownership history — accepted transfers, crew-only (suppliers get []).
  const [transferHistory, setTransferHistory] = useState([]);
  useEffect(() => {
    setTransferHistory([]);
    if (!yacht?.id) return;
    (async () => {
      try {
        const { data } = await api.get(`/yachts/${yacht.id}/transfer-history`);
        setTransferHistory(data || []);
      } catch { /* non-fatal — section stays hidden */ }
    })();
  }, [yacht?.id]);

  const SpecGrid = ({ rows, kind = "engine" }) => {
    if (!rows || rows.length === 0) {
      return <div className="text-[12px] text-zinc-500 italic">No {kind}s recorded.</div>;
    }
    return (
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div
            key={i}
            className="rounded border border-zinc-200 px-3 py-2 text-[13px] grid grid-cols-2 md:grid-cols-5 gap-2"
            data-testid={`detail-${kind}-${i}`}
          >
            <div><span className="text-zinc-500 text-[11px] block">Brand</span>{r.brand}</div>
            <div><span className="text-zinc-500 text-[11px] block">Type</span>{r.type}</div>
            <div><span className="text-zinc-500 text-[11px] block">Power</span>{r.kw ? `${r.kw} kW` : "—"}</div>
            <div><span className="text-zinc-500 text-[11px] block">Hours</span>{r.hours ?? "—"}</div>
            <div className="truncate"><span className="text-zinc-500 text-[11px] block">S/N</span>{r.serial_number || "—"}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={!!yacht} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-2xl gap-0 p-0"
        data-testid="yacht-details-modal"
      >
        {yacht && (
          <>
            <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-100">
              <DialogTitle>{yacht.name}</DialogTitle>
              <DialogDescription>
                {[yacht.brand, yacht.model, yacht.length_m && `${yacht.length_m}m`].filter(Boolean).join(" · ") || "Yacht details"}
              </DialogDescription>
            </DialogHeader>

            <div className="overflow-y-auto px-6 py-4 space-y-4" style={{ maxHeight: "70vh" }}>
              {yacht.cover_image && (
                <div className="rounded-md overflow-hidden bg-zinc-100">
                  <img
                    src={fileUrl(yacht.cover_image)}
                    alt={yacht.name}
                    className="w-full h-auto block"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[13px]">
                {[
                  ["Year", yacht.year],
                  ["Flag", yacht.flag],
                  ["Registration", yacht.registration_no],
                  ["HIN", yacht.hin],
                  ["MMSI", yacht.mmsi, yacht.mmsi ? `https://www.marinetraffic.com/en/ais/details/ships/mmsi:${encodeURIComponent(yacht.mmsi)}` : null],
                  ["Call sign", yacht.call_sign],
                  ["Home marina", yacht.home_marina],
                  ["Currency", yacht.currency],
                  ["Length", yacht.length_m && `${yacht.length_m} m`],
                  ["My role", yacht.my_role],
                ].map(([k, v, href]) => (
                  <div key={k} className="rounded border border-zinc-200 px-3 py-2">
                    <div className="text-[11px] text-zinc-500">{k}</div>
                    <div className="truncate">
                      {v ? (
                        href ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-700 underline underline-offset-2 hover:text-blue-900"
                            title="Live AIS position on MarineTraffic"
                            data-testid="yacht-mmsi-link"
                          >
                            {v}
                          </a>
                        ) : v
                      ) : "—"}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Engines</div>
                <SpecGrid rows={yacht.engines} kind="engine" />
              </div>

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Generators</div>
                <SpecGrid rows={yacht.generators} kind="generator" />
              </div>

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Tenders</div>
                {!yacht.tenders || yacht.tenders.length === 0 ? (
                  <div className="text-[12px] text-zinc-500 italic">No tenders recorded.</div>
                ) : (
                  <div className="space-y-3">
                    {yacht.tenders.map((t, i) => (
                      <div key={i} className="rounded-md border border-zinc-200 p-3" data-testid={`detail-tender-${i}`}>
                        <div className="flex items-start gap-3">
                          {t.cover_image && (
                            <div className="rounded overflow-hidden bg-zinc-100 shrink-0" style={{ width: 200 }}>
                              <img
                                src={fileUrl(t.cover_image)}
                                alt={t.name}
                                className="w-full h-auto block"
                              />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{t.name}</div>
                            <div className="text-[12px] text-zinc-500">
                              {[t.brand, t.model, t.length_m && `${t.length_m}m`].filter(Boolean).join(" · ") || "—"}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2">
                          <SpecGrid rows={t.engines} kind="engine" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Stabilizers</div>
                <SpecGrid rows={yacht.stabilizers} kind="stabilizer" />
              </div>

              {transferHistory.length > 0 && (
                <div data-testid="ownership-history">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Ownership history</div>
                  <div className="space-y-1.5">
                    {transferHistory.map((t) => (
                      <div
                        key={t.id}
                        className="rounded border border-zinc-200 px-3 py-2 text-[13px] flex items-start gap-2"
                        data-testid={`ownership-history-${t.id}`}
                      >
                        <ArrowRightLeft className="h-3.5 w-3.5 mt-0.5 shrink-0 text-zinc-400" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate">
                            <span className="font-medium">{t.from_name}</span>
                            <span className="text-zinc-400"> → </span>
                            <span className="font-medium">{t.to_name}</span>
                          </div>
                          <div className="text-[11px] text-zinc-500">
                            {t.date ? new Date(t.date).toLocaleDateString() : ""}
                            {t.include_crew === false && " · crew did not transfer"}
                            {t.initiator_name && ` · initiated by ${t.initiator_name}`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {yacht.notes && (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Notes</div>
                  <div className="text-[13px] whitespace-pre-wrap text-zinc-700">{yacht.notes}</div>
                </div>
              )}
            </div>

            <DialogFooter className="px-6 py-3 border-t border-zinc-100">
              <Button variant="outline" onClick={onClose} data-testid="close-details">Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
